import { useState } from "preact/hooks";
import Editor from "~/features/editor/editor.tsx";
import Intro from "~/features/intro/intro.tsx";
import type { SetupConfig } from "~/features/setup/config.ts";
import type { SetupFullStep } from "~/features/setup/setup-full.tsx";
import SetupFull from "~/features/setup/setup-full.tsx";
import SetupTry from "~/features/setup/setup-try.tsx";
import {
	I18nProvider,
	useTranslator,
} from "@brickme/frontend-common/src/i18n/translation.tsx";
import { ReferenceProvider, useSystemPalette } from "~/reference/context.tsx";
import { ViewerProvider } from "~/viewer/context.tsx";
import SimplePage from "~/components/simple-page.tsx";
import BuyAKit from "~/features/buy-a-kit/buy-a-kit.tsx";
import type { RegionProviderProps } from "~/hooks/region/context.tsx";
import { RegionProvider } from "~/hooks/region/context.tsx";
import { WorkersProvider } from "~/features/editor/worker-context.tsx";
import { sentryLogExtra } from "~/utils/sentry.ts";
import Layout from "./layout.tsx";

type Setup =
	| { readonly type: "buy" }
	| { readonly type: "full"; readonly step: SetupFullStep }
	| { readonly type: "try" }
	| {
			readonly type: "setup";
			readonly config: SetupConfig;
			readonly previous: Setup;
	  };

function App() {
	const [setup, setSetup] = useState<Setup | undefined>();
	const systemPalette = useSystemPalette();
	const t = useTranslator();

	if (setup?.type === "setup") {
		if (!systemPalette) {
			return (
				<Layout>
					<SimplePage onBackClick={undefined}>
						{t("Loading palette")}...
					</SimplePage>
				</Layout>
			);
		}

		return (
			<Editor
				numberOfKits={setup.config.numberOfKits}
				sourceImage={setup.config.sourceImage}
				systemPalette={systemPalette}
				code={setup.config.code}
				onRestart={() => {
					setSetup(undefined);
				}}
				onBackToUpload={() => {
					setSetup(setup.previous);
				}}
			/>
		);
	}

	return (
		<Layout>
			{!setup && (
				<Intro
					onStartTryItOut={() => {
						setSetup({ type: "try" });
						sentryLogExtra.setMode("try");
					}}
					onStart={() => {
						setSetup({ type: "full", step: { type: "enter-code" } });
						sentryLogExtra.setMode("full");
					}}
					onBuy={() => setSetup({ type: "buy" })}
				/>
			)}
			{setup?.type === "buy" && (
				<BuyAKit onCancel={() => setSetup(undefined)} />
			)}
			{setup?.type === "full" && (
				<SetupFull
					onComplete={(config) =>
						setSetup({ type: "setup", config, previous: setup })
					}
					onCancel={() => setSetup(undefined)}
					step={setup.step}
					onStepChange={(newStep) =>
						setSetup({
							type: "full",
							step: newStep,
						})
					}
				/>
			)}
			{setup?.type === "try" && (
				<SetupTry
					onComplete={(config) =>
						setSetup({ type: "setup", config, previous: setup })
					}
					onCancel={() => setSetup(undefined)}
				/>
			)}
		</Layout>
	);
}

// import hebrewTranslations from "~/i18n/hebrew.json";
const translations = [
	{ name: "English", code: "en", translations: undefined },
	// { name: "עברית (Hebrew)", code: "he", translations: hebrewTranslations },
];

type AppContainerProps = Pick<
	RegionProviderProps,
	"regionConfigs" | "ipInfoToken"
> & {
	readonly userAgent: string;
};

// TODO: Inject translation files
function AppContainer({
	regionConfigs,
	ipInfoToken,
	userAgent,
}: AppContainerProps) {
	return (
		<RegionProvider regionConfigs={regionConfigs} ipInfoToken={ipInfoToken}>
			<ReferenceProvider>
				<ViewerProvider userAgent={userAgent}>
					<I18nProvider translations={translations}>
						<WorkersProvider>
							<App />
							{/* If you want to test sentry error firing, in dev tools make this button larger via styles and click */}
							<button
								style={{
									position: "absolute",
									background: "blue",
									width: 0,
									height: 0,
								}}
								type="button"
								onClick={() => {
									throw new Error("Test error");
								}}
							/>
						</WorkersProvider>
					</I18nProvider>
				</ViewerProvider>
			</ReferenceProvider>
		</RegionProvider>
	);
}

export default AppContainer;
